import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CompleteCheck from 'assets/svg/futures/onboard-complete-check.svg'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import ProgressSteps from 'components/ProgressSteps'
import Spacer from 'components/Spacer'
import { MARGIN_ENGINE_ENABLED } from 'constants/defaults'
import { setOpenModal } from 'state/app/reducer'
import { createPerpsV3Account } from 'state/futures/crossMargin/actions'
import {
	selectCrossMarginAccount,
	selectCrossMarginAvailableMargin,
	selectCrossMarginSupportedNetwork,
	selectMarginEnginePermitted,
} from 'state/futures/crossMargin/selectors'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import DepositCrossMargin from './DepositWithdraw/DepositCrossMargin'

export default function CrossMarginOnboarding() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const crossMarginAvailable = useAppSelector(selectCrossMarginSupportedNetwork)
	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const account = useAppSelector(selectCrossMarginAccount)
	const availableMargin = useAppSelector(selectCrossMarginAvailableMargin)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)

	const [skipDeposit, setSkipDeposit] = useState(false)
	const onClose = useCallback(() => dispatch(setOpenModal(null)), [dispatch])

	const createAccount = useCallback(() => {
		dispatch(createPerpsV3Account())
	}, [dispatch])

	const step = useMemo(() => {
		if (availableMargin.gt(0) || skipDeposit) {
			return 3
		} else if (!!account && (marginEnginePermitted || !MARGIN_ENGINE_ENABLED)) {
			return 2
		}
		// else if (MARGIN_ENGINE_ENABLED && !!account) {
		// 	return 2
		// }
		return 1
	}, [availableMargin, account, skipDeposit, marginEnginePermitted])

	const content = useMemo(() => {
		const totalSteps = MARGIN_ENGINE_ENABLED ? 4 : 3

		if (!crossMarginAvailable) {
			return <ErrorView message={t('futures.modals.onboard.unsupported-network')} />
		}

		switch (step) {
			case 1: {
				return (
					<>
						<Intro>{t('futures.modals.onboard.step1-intro')}</Intro>
						<Spacer height={20} />
						<ProgressSteps step={1} totalSteps={totalSteps} complete={false} />
						<StyledButton
							data-testid="cross-margin-onboard-create-account"
							onClick={createAccount}
							loading={txProcessing}
							disabled={txProcessing}
						>
							{t('futures.modals.onboard.create-account')}
						</StyledButton>
					</>
				)
			}

			// case 2: {
			// 	return (
			// 		<>
			// 			<Intro>{t('futures.modals.onboard.step2-intro-cm')}</Intro>
			// 			<Spacer height={26} />
			// 			<ProgressSteps step={2} totalSteps={totalSteps} complete={false} />
			// 			<StyledButton
			// 				loading={txProcessing}
			// 				disabled={txProcessing}
			// 				data-testid="cross-margin-onboard-permit-margin-engine"
			// 				onClick={() => dispatch(grantMarginEnginePermission())}
			// 			>
			// 				Permit Margin Engine
			// 			</StyledButton>
			// 		</>
			// 	)
			// }

			case 2: {
				return (
					<>
						<Intro>{t('futures.modals.onboard.step3-intro-cm')}</Intro>
						<DepositCrossMargin />
						<Spacer height={26} />
						<ProgressSteps step={2} totalSteps={totalSteps} complete={false} />
						<StyledButton
							data-testid="cross-margin-onboard-deposit"
							onClick={() => setSkipDeposit(true)}
						>
							Skip
						</StyledButton>
					</>
				)
			}

			case 3: {
				return (
					<>
						<Intro>{t('futures.modals.onboard.step4-intro-cm')}</Intro>
						<Complete>
							<CompleteCheck />
						</Complete>
						<Spacer height={26} />
						<ProgressSteps step={3} totalSteps={totalSteps} complete={true} />
						<StyledButton data-testid="cross-margin-onboard-create-account" onClick={onClose}>
							Done
						</StyledButton>
					</>
				)
			}
		}

		return
	}, [step, t, txProcessing, crossMarginAvailable, createAccount, onClose])

	return (
		<StyledBaseModal
			onDismiss={onClose}
			isOpen
			title={t('futures.modals.onboard.cm-title-' + step)}
		>
			{content}
		</StyledBaseModal>
	)
}

const StyledBaseModal = styled(BaseModal)`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	[data-reach-dialog-content] {
		width: 400px;
	}
`

const StyledButton = styled(TransactionButton)`
	margin-top: 24px;
	height: 50px;
	width: 100%;
`

const Intro = styled.div`
	margin-bottom: 15px;
`

const Complete = styled.div`
	padding: 40px;
	text-align: center;
`
